import i18n, { type BackendModule, type ResourceKey } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

type Bundles = Record<string, ResourceKey>;

const languages: Record<string, () => Promise<{ default: Record<string, unknown> }>> = {
  en: () => import('./src/locales/en'),
  tr: () => import('./src/locales/tr'),
};

const availableLanguages = Object.keys(languages);

const pendingBundles: Record<string, Promise<Bundles>> = {};

const loadBundles = (language: string) => {
  pendingBundles[language] ??= languages[language]()
    .then(({ default: files }) => {
      const bundles: Bundles = Object.fromEntries(
        Object.entries(files).map(([path, data]) => [
          path.replace(/^\.\/|\.json$/g, ''),
          data as ResourceKey,
        ])
      );
      Object.entries(bundles).forEach(([namespace, data]) =>
        i18n.addResourceBundle(language, namespace, data, true, true)
      );
      return bundles;
    })
    .catch((error) => {
      delete pendingBundles[language];
      throw error;
    });

  return pendingBundles[language];
};

const bundledBackend: BackendModule = {
  type: 'backend',
  init: () => undefined,
  read: (language, namespace, callback) => {
    if (!languages[language]) {
      callback(null, {});
      return;
    }

    loadBundles(language)
      .then((bundles) => callback(null, bundles[namespace] ?? {}))
      .catch((error) => callback(error, false));
  },
};

export const i18nReady = i18n
  .use(bundledBackend)
  .use(LanguageDetector) // Kullanıcının dilini algılar
  .use(initReactI18next) // i18n örneğini react-i18next'e geçirir
  .init({
    // 'whitelist' seçeneği kullanımdan kaldırılmıştır. Yerine 'supportedLngs' kullanılır.
    supportedLngs: availableLanguages,

    fallbackLng: 'en', // Desteklenmeyen bir dil seçilirse kullanılacak varsayılan dil

    // Dil algılama seçenekleri
    detection: {
      // Dilin hangi sırayla nereden aranacağını belirtir.
      // Dil tercihi yoksa fallbackLng ile belirlenen İngilizce kullanılır.
      order: ['localStorage'],

      // Algılanan dili nereye kaydedeceğini belirtir.
      caches: ['localStorage'],
    },
    load: 'currentOnly',
    defaultNS: 'common', // Varsayılan namespace
    interpolation: {
      escapeValue: false, // React zaten XSS'e karşı koruma sağladığı için gereksizdir.
    },

    // Sadece geliştirme ortamında debug modunu açmak daha iyidir.

    react: {
      useSuspense: false, // React Suspense kullanımını devre dışı bırakır.
    },
  });

export default i18n;
